/**
 * Add Entity Modal
 * Form to create new entities (Metrics, Projects, CSFs, Constraints, Initiatives)
 */

import React, { useState } from 'react';
import useStore from '../store/useStore';
import './AddEntityModal.css';

const AddEntityModal = ({ onClose }) => {
  const { createEntity, selectedAsset } = useStore();
  const [formData, setFormData] = useState({
    Title: '',
    EntityType: 'Metric',
     Category: 'Production',
    TargetValue: '',
    TargetUnit: '',
    Description: '',
    Owner: '',
    OwnerEmail: '',
    SupervisorEmail: '',
    Weight: '',
    Deadline: '',
    IsCompleted: false
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.Title.trim()) {
      newErrors.Title = 'Entity name is required';
    }
    
    if (!formData.EntityType) {
      newErrors.EntityType = 'Entity type is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Update handleSubmit entityData:

const entityData = {
  Title: formData.Title,
  EntityType: formData.EntityType,
  Category: formData.Category, 
  Description: formData.Description,
  Owner: formData.Owner || null,
  OwnerEmail: formData.OwnerEmail || null,
  SupervisorEmail: formData.SupervisorEmail || null,
  Weight: formData.Weight ? parseFloat(formData.Weight) : null,
  Deadline: formData.Deadline || null,
  IsCompleted: formData.IsCompleted
};

      // Add target value if provided
      if (formData.TargetValue) {
        entityData.TargetValue = parseFloat(formData.TargetValue);
        entityData.TargetUnit = formData.TargetUnit || '';
      }

      await createEntity(entityData);
      onClose();
    } catch (error) {
      alert('Failed to create entity. Please try again.');
      console.error('Error creating entity:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add New Entity</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        
        <form onSubmit={handleSubmit} className="entity-form">
          <div className="form-group">
            <label htmlFor="Title">Entity Name *</label>
            <input
              type="text"
              id="Title"
              name="Title"
              value={formData.Title}
              onChange={handleChange}
              placeholder="e.g., Crude Oil Production, Phase 4 Development"
              className={errors.Title ? 'error' : ''}
              disabled={isSubmitting}
            />
            {errors.Title && <span className="error-message">{errors.Title}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="EntityType">Entity Type *</label>
            <select
              id="EntityType"
              name="EntityType"
              value={formData.EntityType}
              onChange={handleChange}
              className={errors.EntityType ? 'error' : ''}
              disabled={isSubmitting}
            >
              <option value="Metric">Metric</option>
              <option value="Project">Project</option>
              <option value="CSF">CSF (Critical Success Factor)</option>
              <option value="Constraint">Constraint</option>
              <option value="Initiative">Initiative</option>
            </select>
            {errors.EntityType && <span className="error-message">{errors.EntityType}</span>}
            <small className="help-text">
              {formData.EntityType === 'Metric' && 'Production metrics to track (e.g., BOPD, Gas Production)'}
              {formData.EntityType === 'Project' && 'Development projects that impact production'}
              {formData.EntityType === 'CSF' && 'Critical factors for success'}
              {formData.EntityType === 'Constraint' && 'Limitations or blockers'}
              {formData.EntityType === 'Initiative' && 'Strategic initiatives'}
            </small>
          </div>
          {(formData.EntityType === 'Metric') && (
            <div className="form-group">
              <label htmlFor="Category">Category</label>
              <select
                id="Category"
                name="Category"
                value={formData.Category}
                onChange={handleChange}
                disabled={isSubmitting}
              >
                <option value="Production">Production</option>
                <option value="HSE">HSE (Health, Safety, Environment)</option>
              </select>
              <small className="help-text">
                {formData.Category === 'Production' && 'Production metrics (Oil, Gas, Water)'}
                {formData.Category === 'HSE' && 'Safety metrics (POB, UA/UC, PTW, LTI, Toolbox)'}
              </small>
            </div>)}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="TargetValue">Target Value</label>
              <input
                type="number"
                id="TargetValue"
                name="TargetValue"
                value={formData.TargetValue}
                onChange={handleChange}
                step="0.01"
                placeholder="e.g., 5523"
                disabled={isSubmitting}
              />
              <small className="help-text">Numeric target or goal</small>
            </div>

            <div className="form-group">
              <label htmlFor="TargetUnit">Unit</label>
              <input
                type="text"
                id="TargetUnit"
                name="TargetUnit"
                value={formData.TargetUnit}
                onChange={handleChange}
                placeholder="e.g., BOPD, %, Score"
                disabled={isSubmitting}
              />
              <small className="help-text">Unit of measurement</small>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="Owner">Owner (Optional)</label>
            <input
              type="text"
              id="Owner"
              name="Owner"
              value={formData.Owner}
              onChange={handleChange}
              placeholder="e.g., John Doe, Engineering Team"
              disabled={isSubmitting}
            />
            <small className="help-text">Person or team responsible</small>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="OwnerEmail">Owner Email (Optional)</label>
              <input
                type="email"
                id="OwnerEmail"
                name="OwnerEmail"
                value={formData.OwnerEmail}
                onChange={handleChange}
                placeholder="owner@company.com"
                disabled={isSubmitting}
              />
              <small className="help-text">Used for email notifications</small>
            </div>

            <div className="form-group">
              <label htmlFor="SupervisorEmail">Supervisor Email (Optional)</label>
              <input
                type="email"
                id="SupervisorEmail"
                name="SupervisorEmail"
                value={formData.SupervisorEmail}
                onChange={handleChange}
                placeholder="supervisor@company.com"
                disabled={isSubmitting}
              />
              <small className="help-text">CC'd on alert emails</small>
            </div>
          </div>
<div className="form-group">
  <label htmlFor="Weight">Weight (0-100)</label>
  <input
    type="number"
    id="Weight"
    name="Weight"
    value={formData.Weight}
    onChange={handleChange}
    step="0.5"
    min="0"
    max="100"
    placeholder="e.g., 30"
    disabled={isSubmitting}
  />
  <small className="help-text">Performance weight (0 = lowest, 100 = highest importance)</small>
</div>
          {/* Deadline and Completion fields - only for Projects and Initiatives */}
          {(formData.EntityType === 'Project' || formData.EntityType === 'Initiative') && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="Deadline">Deadline</label>
                  <input
                    type="date"
                    id="Deadline"
                    name="Deadline"
                    value={formData.Deadline}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                  <small className="help-text">Project completion deadline</small>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="IsCompleted"
                      checked={formData.IsCompleted}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    />
                    <span>Mark as Completed</span>
                  </label>
                  <small className="help-text">Check if project is finished</small>
                </div>
              </div>
            </>
          )}

          <div className="form-group">
            <label htmlFor="Description">Description (Optional)</label>
            <textarea
              id="Description"
              name="Description"
              value={formData.Description}
              onChange={handleChange}
              rows="3"
              placeholder="Additional details about this entity"
              disabled={isSubmitting}
            />
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="btn-cancel" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Entity'}
            </button>
          </div>
        </form>

        <div className="modal-info">
          <strong>Asset:</strong> {selectedAsset?.Title}
        </div>
      </div>
    </div>
  );
};

export default AddEntityModal;